/* Progressive C1 mobile UI. The existing purchase controller remains the only owner of prices, inventory and cart submission. */
(() => {
  const mq = window.matchMedia('(max-width:749px)');
  const cleanup = new Map();
  const waiting = new Map();
  let headerObserver;
  const isC1 = () => document.body.hasAttribute('data-er-c1-mobile-template');

  function initHeaderOffset() {
    const header = document.getElementById('header-group');
    if (!header || headerObserver) return;
    const measure = () => document.body.style.setProperty('--er-c1-header-height', `${Math.ceil(header.getBoundingClientRect().height)}px`);
    measure();
    if ('ResizeObserver' in window) {
      headerObserver = new ResizeObserver(measure);
      headerObserver.observe(header);
    }
  }

  function initPurchase(root) {
    if (cleanup.has(root)) return;
    if (root.dataset.erpdpInitialized !== 'true') {
      if (!waiting.has(root)) {
        const observer = new MutationObserver(() => {
          if (root.dataset.erpdpInitialized !== 'true') return;
          observer.disconnect();
          waiting.delete(root);
          initPurchase(root);
        });
        observer.observe(root, {attributes: true, attributeFilter: ['data-erpdp-initialized']});
        waiting.set(root, observer);
      }
      return;
    }
    const select = root.querySelector('[data-er-c1-variant-select]');
    const buttons = [...root.querySelectorAll('[data-erpdp-variant]')];
    const input = root.querySelector('[data-erpdp-variant-id]');
    if (!select || !input || !buttons.length) return;
    const abort = new AbortController();
    const listeners = {signal: abort.signal};
    const sync = () => { select.value = input.value; };
    sync();
    select.hidden = false;
    root.dataset.erC1SelectReady = 'true';
    select.addEventListener('change', () => {
      const target = buttons.find(button => button.dataset.erpdpVariant === select.value);
      if (target) target.click();
      sync();
    }, listeners);
    root.addEventListener('click', event => {
      if (event.target.closest('[data-erpdp-variant]')) sync();
    }, listeners);
    input.addEventListener('change', sync, listeners);
    const sticky = root.querySelector('[data-erpdp-mobile-cart]');
    const main = root.querySelector('[data-erpdp-add]');
    let observer;
    if (sticky && main && 'IntersectionObserver' in window) {
      observer = new IntersectionObserver(entries => {
        const entry = entries[0];
        sticky.classList.toggle('er-c1-sticky-away', entry.isIntersecting && entry.intersectionRatio >= 0.5);
      }, {threshold: [0, 0.5, 1]});
      observer.observe(main);
    }
    cleanup.set(root, () => {
      abort.abort();
      observer?.disconnect();
      sticky?.classList.remove('er-c1-sticky-away');
      select.hidden = true;
      delete root.dataset.erC1SelectReady;
    });
  }

  function initSection(section) {
    if (cleanup.has(section)) return;
    const abort = new AbortController();
    const listeners = {signal: abort.signal};
    section.querySelectorAll('[data-er-c1-modes]').forEach(group => {
      const controls = group.querySelector('.er-c1-mode-controls');
      const buttons = [...group.querySelectorAll('[data-er-c1-mode]')];
      const panels = [...group.querySelectorAll('[data-er-c1-mode-panel]')];
      if (!controls || !buttons.length || !panels.length) return;
      function selectMode(id) {
        if (!panels.some(panel => panel.dataset.erC1ModePanel === id)) return;
        buttons.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.erC1Mode === id)));
        panels.forEach(panel => { panel.hidden = panel.dataset.erC1ModePanel !== id; });
      }
      controls.hidden = false;
      selectMode(buttons.find(button => button.getAttribute('aria-pressed') === 'true')?.dataset.erC1Mode || buttons[0].dataset.erC1Mode);
      buttons.forEach((button, index) => {
        button.addEventListener('click', () => selectMode(button.dataset.erC1Mode), listeners);
        button.addEventListener('keydown', event => {
          let next;
          if (event.key === 'ArrowRight') next = (index + 1) % buttons.length;
          if (event.key === 'ArrowLeft') next = (index + buttons.length - 1) % buttons.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = buttons.length - 1;
          if (next === undefined) return;
          event.preventDefault();
          buttons[next].focus();
          selectMode(buttons[next].dataset.erC1Mode);
        }, listeners);
      });
      group.addEventListener('shopify:block:select', event => {
        const panel = event.target.closest('[data-er-c1-mode-panel]');
        if (panel) selectMode(panel.dataset.erC1ModePanel);
      }, listeners);
    });
    cleanup.set(section, () => {
      abort.abort();
      section.querySelectorAll('.er-c1-mode-controls').forEach(controls => { controls.hidden = true; });
      section.querySelectorAll('[data-er-c1-mode-panel]').forEach(panel => { panel.hidden = false; });
    });
  }

  function revealHash() {
    if (!isC1() || !mq.matches) return;
    const id = location.hash.slice(1);
    const mapped = {
      specifications: 'c1-mobile-specifications', compatibility: 'c1-mobile-modes',
      playbook: 'c1-mobile-modes', performance: 'c1-mobile-performance',
      care: 'c1-mobile-details', faq: 'c1-mobile-details', story: 'c1-mobile-pack',
      highlights: 'c1-mobile-hero'
    }[id] || id;
    const target = document.getElementById(mapped);
    if (!target) return;
    const detail = target.closest('details');
    const opened = detail && !detail.open;
    if (detail) detail.open = true;
    if (mapped !== id || opened) requestAnimationFrame(() => target.scrollIntoView({behavior: 'instant', block: 'start'}));
  }

  function init() {
    if (!isC1()) return;
    initHeaderOffset();
    document.querySelectorAll('[data-erpdp-purchase]').forEach(initPurchase);
    if (mq.matches) document.querySelectorAll('[data-er-c1-section]').forEach(initSection);
    revealHash();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, {once: true});
  else init();
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', event => {
    for (const [root, dispose] of cleanup) {
      if (event.target === root || event.target.contains(root)) { dispose(); cleanup.delete(root); }
    }
    for (const [root, observer] of waiting) {
      if (event.target === root || event.target.contains(root)) { observer.disconnect(); waiting.delete(root); }
    }
  });
  mq.addEventListener('change', () => { if (mq.matches) init(); });
  window.addEventListener('hashchange', revealHash);
  document.addEventListener('click', event => {
    if (!isC1() || !mq.matches) return;
    const link = event.target.closest('a[href^="#"]');
    if (!link) return;
    const target = document.getElementById(link.getAttribute('href').slice(1));
    const detail = target?.closest('details');
    if (detail) detail.open = true;
  });
})();
